import api from "@/api/v1-jwt/config/api";
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { createErrorHandler } from "@/utils/errorHandler";
import paymentMethodQueryKeys from "./query-keys";
import { z } from "zod";

const ResponseSchema = z.object({
  default: z.boolean(),
  payment_method_id: z.string(),
  user_id: z.string(),
});

type Response = z.infer<typeof ResponseSchema>;

type UpdateDefaultPaymentMethodParams = {
  userId: string;
  paymentMethodId: string;
};

const updateDefaultPaymentMethod = async ({
  userId,
  paymentMethodId,
}: UpdateDefaultPaymentMethodParams): Promise<Response> => {
  const url = `/platform/payment_methods/set_default/${userId}`;
  const payload = {
    user_id: userId,
    payment_method_id: paymentMethodId,
  };
  const { data } = await api.patch(url, payload);
  return data?.data;
};

type UpdatePaymentMethodMutationOptions = UseMutationOptions<
  Response,
  Error,
  UpdateDefaultPaymentMethodParams
>;

export const useUpdateDefaultPaymentMethodOptions = (
  options?: UpdatePaymentMethodMutationOptions
): UpdatePaymentMethodMutationOptions => {
  const queryClient = useQueryClient();
  const onSettled = options?.onSettled;
  const defaultOptions: UpdatePaymentMethodMutationOptions = {
    mutationFn: updateDefaultPaymentMethod,
    onSettled: (...params) => {
      queryClient.invalidateQueries(paymentMethodQueryKeys.all);
      if (onSettled) {
        onSettled(...params);
      }
    },
    onError: createErrorHandler(),
  };
  return { ...defaultOptions, ...options };
};

export const useUpdateDefaultPaymentMethod = (
  options?: UpdatePaymentMethodMutationOptions
) => {
  const mutationOptions = useUpdateDefaultPaymentMethodOptions(options);
  return useMutation<Response, Error, UpdateDefaultPaymentMethodParams>(
    mutationOptions
  );
};

export default useUpdateDefaultPaymentMethod;
